import { useTheme } from '@repo/theme';
import { UtilityText } from 'component/text';
import { useEffect, useEffectEvent, useState } from 'react';
import { View } from 'react-native';

import { CircularBar } from './circular-bar';

export function useCountdown(timer = 60, interval = 1000) {
    const [count, setCount] = useState(timer);
    const [countdownFinished, setCountDownFinished] = useState(false);

    useEffect(() => {
        if (process.env.NODE_ENV === 'test') {
            return;
        }

        const timerId = setInterval(() => {
            setCount((prevCount) => {
                const newCount = prevCount - interval / 1000;
                if (newCount <= 0) {
                    setCountDownFinished(true);
                    clearInterval(timerId);
                    return 0;
                }
                return newCount;
            });
        }, interval);

        return () => clearInterval(timerId);
    }, [timer, interval]);

    // HACK: countdown causes test randomness, so we skip it in test mode
    if (process.env.NODE_ENV === 'test') {
        return { count: timer, countdownFinished: false, setCount: () => {} };
    }

    return { count, countdownFinished, setCount };
}
export type VideoVotingCountdownElementProps = {
    size?: number;
    circleRadius?: number;
    strokeColor?: string;
    strokeBackgroundColor?: string;
    backgroundColor?: string;
    strokeWidth?: number;
    timer: number;
    callback: any;
};
/**
 * A circular countdown element which runs down in the given time and executes a
 * callback
 */
export function VideoVotingCountdownElement({
    size = 48,
    circleRadius = 18,
    strokeColor = '#484848',
    strokeBackgroundColor = '#00000040',
    backgroundColor,
    strokeWidth = 4,
    timer,
    callback,
}: VideoVotingCountdownElementProps) {
    const { colors } = useTheme();
    const resolvedBackgroundColor = backgroundColor ?? colors.colorSurfacePrimary;
    const { count, countdownFinished, setCount } = useCountdown(timer, 100);
    const fireCallback = useEffectEvent(() => callback?.());
    useEffect(() => {
        if (!countdownFinished) return;
        fireCallback();
    }, [countdownFinished]);

    useEffect(() => {
        setCount(timer);
    }, [timer, setCount]);

    return (
        <View style={{ backgroundColor: resolvedBackgroundColor, borderRadius: circleRadius * 2 }}>
            <CircularBar
                circleRadius={circleRadius}
                percentage={(count * 100) / timer}
                size={size}
                strokeBackgroundColor={strokeBackgroundColor}
                strokeColor={strokeColor}
                strokeWidth={strokeWidth}
            >
                <UtilityText text={Math.ceil(count).toString()}></UtilityText>
            </CircularBar>
        </View>
    );
}
